import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'react-hot-toast';

import './index.css';

import App from './App';
import i18n from './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { VoiceProvider } from './contexts/VoiceContext';
import { SocketProvider } from './contexts/SocketContext';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Create luxury theme (Livestock Management UX)
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#10b981', // Emerald
      light: '#d1fae5',
      dark: '#064e3b', // Forest
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0f172a', // Navy
      light: '#1e293b',
      dark: '#020617',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fcfdfe',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "system-ui", sans-serif',
    h1: { fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontFamily: 'Montserrat', fontWeight: 700, letterSpacing: '-0.01em' },
    h4: { fontFamily: 'Montserrat', fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontFamily: 'Montserrat', fontWeight: 600 },
    h6: { fontFamily: 'Montserrat', fontWeight: 600 },
    button: { fontFamily: 'Montserrat', fontWeight: 700, textTransform: 'none' },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #10b981 0%, #064e3b 100%)',
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(16, 185, 129, 0.35)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          transition: 'all 0.4s ease',
          '&:hover': {
            boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #e2e8f0',
          color: '#0f172a',
          boxShadow: 'none',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
            <SocketProvider>
              <AuthProvider>
                <OfflineProvider>
                  <VoiceProvider>
                    <App />
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#363636',
                          color: '#fff',
                        },
                        success: {
                          duration: 3000,
                          iconTheme: {
                            primary: '#10b981',
                            secondary: '#fff',
                          },
                        },
                        error: {
                          duration: 5000,
                          iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                          },
                        },
                      }}
                    />
                  </VoiceProvider>
                </OfflineProvider>
              </AuthProvider>
            </SocketProvider>
          </BrowserRouter>
        </ThemeProvider>
      </I18nextProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
    </QueryClientProvider>
  </React.StrictMode>
);
// Register Service Worker and handle updates
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        console.log('SW registered: ', registration);
        
        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000 * 5); // Every 5 minutes

        registration.onupdatefound = () => {
          const installingWorker = registration.installing;
          if (installingWorker) {
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  console.log('SW: New content available; refreshing.');
                  // For the Demo: silent reload if a new SW is waiting
                  window.location.reload();
                }
              }
            };
          }
        };
      })
      .catch(registrationError => {
        console.log('SW registration failed: ', registrationError);
      });
  });

  // Reload the page when the service worker changes
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      refreshing = true;
      window.location.reload();
    }
  });
}
